import {useCallback, useEffect, useState} from 'react';

import type {PaginationProps} from '@/components/common/Pagination';
import {transactionService} from '@/services/transactionService';
import type {AfriexAdminTransaction, AfriexQueryParams} from '@/types';

const useRewardsTransactions = (userId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [transactions, setTransactions] = useState<AfriexAdminTransaction[]>(
    [],
  );
  const [pagination, setPagination] = useState<PaginationProps>({
    total: 0,
    limit: 10,
  });
  const [isPageReset] = useState(false);

  const getRewardTransactions = useCallback(
    async (params: AfriexQueryParams) => {
      try {
        setLoading(true);
        const txns = await transactionService.getRewardTransactions(userId, {
          limit: pagination.limit,
          ...params,
        });

        setTransactions(txns.data);
        setPagination({
          total: txns.total,
          limit: txns.limit,
        });
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [userId, pagination.limit],
  );

  useEffect(() => {
    // Fire-and-forget: getRewardTransactions catches its own failures into
    // `error`, so this only stops an unexpected throw becoming an unhandled
    // rejection.
    getRewardTransactions({page: 0}).catch(() => {}); // Start at page 0
  }, [getRewardTransactions]);

  return {
    error,
    loading,
    transactions,
    getRewardTransactions,
    pagination,
    isPageReset,
  };
};

export default useRewardsTransactions;
